// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import { dangerouslyFormat } from 'app/common/lib/StringUtil';
import { Avatar, Button, Box, Icon, Text, Touchable } from 'gestalt';

type ContactProps = {
  body?: string,
  img: React.Element<any>,
  onClick: () => void,
  selected: boolean,
  title: string,
  username?: string,
  valid?: boolean,
};

export default function Contact(props: ContactProps) {
  const { body, img, onClick, selected, title, username, valid } = props;
  const subtitle = username || body;

  const selectIcon = (isSelected, isEmailValid) => {
    if (username) {
      return isSelected ? (
        <Icon
          icon="check-circle"
          color="red"
          accessibilityLabel={i18n._(
            'Confirmation an invite will be sent',
            'Alt tags on a check box on the board create flow for board invites',
          )}
          size={32}
        />
      ) : (
        <Box
          height={32}
          width={32}
          shape="circle"
          dangerouslySetInlineStyle={{ __style: { border: 'solid 1px #B5B5B5' } }}
        />
      );
    } else {
      const buttonProps = {
        text: i18n._('Sent', 'Sent a board collaborator invite'),
        color: 'gray',
        size: 'sm',
        disabled: true,
        inline: true,
      };
      if (!isSelected) {
        buttonProps.text = i18n._('Invite', 'Add board collaborator invite');
        buttonProps.disabled = Boolean(!isEmailValid);
      }
      return <Button {...buttonProps} />;
    }
  };

  return (
    <Box marginBottom={4}>
      <Touchable onTouch={onClick}>
        <Box display="flex" alignItems="center" direction="row" width="100%">
          {img}
          <Box marginLeft={3} flex="grow" marginRight={2}>
            <Box marginBottom={1}>
              <Text bold size="md" truncate>
                {title}
              </Text>
            </Box>
            <Text size="xs" truncate>
              {subtitle}
            </Text>
          </Box>
          <Box marginLeft={2}>{selectIcon(selected, valid)}</Box>
        </Box>
      </Touchable>
    </Box>
  );
}

export function makeContact(
  contact: Object,
  selected: boolean,
  onClick: (collaborator: Object) => void,
) {
  if (contact.type === 'user_share_suggestion' || contact.type === 'user') {
    const img = (
      <Avatar name={contact.title || contact.full_name} src={contact.image_medium_url} size="md" />
    );
    return (
      <Contact
        img={img}
        onClick={() => onClick(contact)}
        selected={selected}
        title={contact.full_name}
        username={contact.username}
      />
    );
  } else if (contact.type === 'email') {
    const { id, valid } = contact;
    const icon = (
      <Box
        shape="circle"
        color="lightGray"
        height={40}
        width={40}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Icon
          color="darkGray"
          icon="envelope"
          size={20}
          accessibilityLabel={i18n._('Email icon', 'Email icon on board create modal')}
        />
      </Box>
    );
    const title = dangerouslyFormat(i18n._('Email {{ email }}'), { email: id });
    let subtitle, handleEmailContactClick;
    if (selected) {
      subtitle = i18n._('Done!', 'Text that lets the user know we sent the board invite email');
      handleEmailContactClick = () => undefined;
    } else {
      subtitle = valid
        ? i18n._(
            'Click to send!',
            'Text that lets the user know they can successfully send a board invite email',
          )
        : i18n._(
            'Keep typing!',
            'Text that lets a user know the email they are typing is not valid yet',
          );
      handleEmailContactClick = () => (valid ? onClick(contact) : undefined);
    }

    return (
      <Contact
        body={subtitle}
        img={icon}
        onClick={handleEmailContactClick}
        selected={selected}
        title={title}
        valid={valid}
      />
    );
  }
  return undefined;
}
